// src/utils/getHomePathForRole.js
//
// WHERE SIGNING IN LANDS YOU: the first page the SIDEBAR actually shows.
//
// It used to be the first path `generateMenu` produced, which is only half the
// story. The sidebar takes that same list and then filters it twice more —
// by page permission (`hasPageAccess`) and by the active phase — so the entry
// this returned could be one the sidebar had already dropped. Signing in then
// landed somewhere that was not the top of the menu, and sometimes on a page
// that was not in the menu at all.
//
// Applying the same two filters here is what makes "the landing page" and "the
// first thing in the sidebar" the same sentence. The phone does the same thing
// a different way — its drawer navigator is told to open on `pages[0].path` —
// because there the menu is already the filtered list.

import menusByRole from '../config/roleMenus';
import { hasPageAccess } from '../config/pagePermissions';

function normalizeRole(role) {
  const r = (role || '').toString().trim();
  if (!r) return '';
  const lower = r.toLowerCase();

  if (lower === 'principalheadteacher') return 'Principal';
  if (lower === 'guardian') return 'Parent';
  if (lower === 'superadmin') return 'SystemAdmin';

  return r;
}

/** The same phase test the sidebar applies. An unset phase allows everything. */
function phaseAllows(itemPhase, activePhase) {
  if (!itemPhase) return true;
  if (!activePhase) return true;
  if (Array.isArray(itemPhase)) return itemPhase.includes(activePhase);
  return String(itemPhase).toLowerCase() === activePhase;
}

/**
 * The first menu path this user can actually open, walking groups before their
 * children exactly as the sidebar renders them.
 */
function firstVisiblePath(menuItems, { roles, privileges, activePhase }) {
  if (!Array.isArray(menuItems)) return null;

  for (const item of menuItems) {
    if (!item) continue;
    if (!phaseAllows(item.phase, activePhase)) continue;

    // A leaf is offered only if the permission table agrees, which is the check
    // the sidebar makes before drawing it.
    if (item.path && hasPageAccess(roles, item.path, privileges)) return item.path;

    if (Array.isArray(item.subMenus)) {
      const sub = firstVisiblePath(item.subMenus, { roles, privileges, activePhase });
      if (sub) return sub;
    }
  }
  return null;
}

export function getHomePathForRole(role, privileges = []) {
  const normalized = normalizeRole(role);
  let phase = null;
  try {
    phase = localStorage.getItem('schoolAdmin_activePhase') || null;
  } catch (_) {}

  const privs = Array.isArray(privileges) ? privileges : [];
  // Privileges count as roles for the permission table — a Teacher who is also
  // a ClassMaster reaches /classmaster/* through the second, not the first.
  const roles = [normalized, ...privs].filter(Boolean);

  // The same menu generator the sidebar uses, so staff roles resolve to a real
  // first page rather than to a hard-coded guess per role.
  const menu = (typeof menusByRole.generateMenu === 'function')
    ? menusByRole.generateMenu([normalized], { phase, privileges: privs })
    : (menusByRole[normalized] || menusByRole[(normalized || '').toString()] || []);

  const path = firstVisiblePath(menu, { roles, privileges: privs, activePhase: phase });
  if (path) return path;

  // Safe fallbacks (should be rare).
  const lower = (normalized || '').toLowerCase();
  if (lower === 'systemadmin') return '/super-admin/system-overview';
  if (lower === 'parent') return '/parent/dashboard';
  if (lower === 'student') return '/communication/messages';
  return '/';
}
